import {makeAutoObservable} from 'mobx';
import {ContactFormStore} from './ContactForm/ContactFormStore/ContactFormStore';
import CustomerPortalStore from './Customer/CustomerPortalStore/CustomerPortalStore';
import {Contact} from './types';
import {User} from '../Auth/User';
import {ParameterStore} from '../Core/mobx/ParameterStore/ParameterStore';
import type {RootStore} from '../Core/mobx/RootStore';
import {AB_FLAGS} from '../Core/constants/abFlags';
import {v4 as uuidv4} from 'uuid';

export class AgentStore {
    customerPortalStore = new CustomerPortalStore();

    parameterStore = new ParameterStore();

    contactsFormStore = new ContactFormStore(this);

    constructor(private parentStore: RootStore) {
        makeAutoObservable(this);
    }

    reset() {
        this.parameterStore = new ParameterStore();
        this.contactsFormStore = new ContactFormStore(this);
        this.customerPortalStore = new CustomerPortalStore();
    }

    getAgentsList() {
        return this.parentStore.agentsList;
    }

    async selectForm({id, email, historyId}: {id: string; email: string; historyId?: string}) {
        this.parentStore.uiStore.showSnackbar('Getting form...', 'info');
        this.parameterStore.loadingClientData = true;

        const form = await this.contactsFormStore.selectForm({id, email, historyId});

        if (form?.customerPortalCustomerID) {
            await this.selectCustomerById(form.customerPortalCustomerID);
        }

        this.parameterStore.loadingClientData = false;
        this.parentStore.uiStore.showSnackbar('Form ready!', 'success');

        return form;
    }

    async getForm({id, email, historyId}: {id: string; email: string; historyId?: string}) {
        return this.contactsFormStore.getForm({id, email, historyId});
    }

    async startFamilyForm({id, email}: {id: string; email: string}) {
        this.parentStore.uiStore.showSnackbar('Getting form for family...', 'info');
        this.parameterStore.loadingClientData = true;

        const form = await this.contactsFormStore.startFamilyForm({id, email});

        if (form?.customerPortalCustomerID) {
            await this.selectCustomerById(form.customerPortalCustomerID);
        }

        this.parameterStore.loadingClientData = false;
        this.parentStore.uiStore.showSnackbar('Form ready!', 'success');

        return form;
    }

    async startNewForm(client: Contact) {
        this.reset();

        this.contactsFormStore.formCreatedAt = new Date();
        this.contactsFormStore.contact = client.email;
        this.contactsFormStore.name = `${client.firstName} ${client.lastName}`;
        this.contactsFormStore.hs_object_id = client.id;
        this.contactsFormStore.hubspot_owner_id = client.ownerId;

        // Generate form ID if not already set
        const formId = this.parameterStore.formid || uuidv4();
        if (!this.parameterStore.formid) {
            this.parameterStore.formid = formId;
        }

        // Calculate AB flags for this form
        const abFlagsStore = this.parentStore.abFlagsStore;
        if (abFlagsStore.isInitialized) {
            const lowerHouseholdIncomeLimit = abFlagsStore.calculateFormFlag(
                AB_FLAGS.LOWER_HOUSEHOLD_INCOME_LIMIT,
                formId,
            );

            // Store the AB flag in the parameter store
            this.parameterStore.abFlags = {
                [AB_FLAGS.LOWER_HOUSEHOLD_INCOME_LIMIT]: lowerHouseholdIncomeLimit,
            };

            console.log(
                `✅ Calculated lowerHouseholdIncomeLimit flag for form ${formId}: ${lowerHouseholdIncomeLimit}`,
            );
        } else {
            console.warn('AB flags store not initialized when creating new form');
        }

        this.selectClient(client);
    }

    selectClient(client: Contact) {
        const clientData = {
            firstName: client.firstName,
            lastName: client.lastName,
            emailAddress: client.email,
            phoneNumber: client.phone,
            address: client.address,
            addressZipCode: client.zip,
            birthCountry: '',
            addressCity: client.city,
            agentName: User.Info.name,
            hsLeadStatus: client.leadStatus,
            notes: client.notes,
        };

        this.parameterStore.updateApproval(clientData);
    }

    private async selectCustomerById(id: string) {
        return this.customerPortalStore.selectCustomerById(id);
    }
}
